import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Send, X, Loader2, Volume2, VolumeX, Info, Server, Clock, Moon, RotateCcw, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Markdown } from "@/components/Markdown";
import BitAvatar from "@/components/bit/BitAvatar";
import { buildTaskSummaryAndPraise } from "@/lib/bit";

const DISCLAIMER_KEY = "devpath_ai_disclaimer_dismissed";
const VOICE_KEY = "devpath_bit_voice";
const SLEEP_KEY = "devpath_bit_hidden";

const PRAISE = [
  "Stark! Die Leiterbahn leuchtet.",
  "Richtig! Das war sauber gelöst.",
  "Treffer! Weiter so.",
  "Genau so. Du hast den Dreh raus.",
];

const CHIPS = ["Gib mir einen Tipp", "Erklär das einfacher", "Zeig mir ein Beispiel"];

const IDLE_ASK_MS = 45_000;
const IDLE_SLEEP_MS = 90_000;

function newSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function readFlag(key) {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key, on) {
  try {
    if (on) localStorage.setItem(key, "1");
    else localStorage.removeItem(key);
  } catch {
    // Private mode etc. - the preference just won't stick.
  }
}

/** Markdown answers read badly aloud; keep the prose, drop code and markup. */
function speakable(text) {
  return text
    .replace(/```[\s\S]*?```/g, " (Codebeispiel im Chat) ")
    .replace(/[`*_#>]/g, "")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")
    .slice(0, 500);
}

export default function BitCompanion() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => readFlag(SLEEP_KEY));
  const [bubble, setBubble] = useState(null); // {text, action?: {label, run}}
  const [mood, setMood] = useState("neutral");
  const [anim, setAnim] = useState("");
  const [talking, setTalking] = useState(false);
  const [look, setLook] = useState({ x: 0, y: 0 });
  const [voice, setVoice] = useState(() => readFlag(VOICE_KEY));
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [context, setContext] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [floatingUp, setFloatingUp] = useState(false);
  const [floatingBottom, setFloatingBottom] = useState(null);
  const [taskBubble, setTaskBubble] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "ai",
      text: "Hallo, ich bin **Bit**, dein Lernroboter. Frag mich alles übers Programmieren, ich erkläre dir Konzepte und deinen Code.",
    },
  ]);

  const sessionRef = useRef(newSessionId());
  const buttonRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const timers = useRef({});
  const lastActive = useRef(Date.now());
  const idleState = useRef("active");
  const failStreak = useRef(0);
  const voiceRef = useRef(voice);
  voiceRef.current = voice;
  const lastContextRef = useRef(null);
  const pendingChatFeedback = useRef(null);

  useEffect(() => {
    if (context) lastContextRef.current = context;
  }, [context]);

  const lastUserMessage = [...messages].reverse().find((m) => m.role === "user");
  const lastUserQuestion = lastUserMessage?.text;

  const later = useCallback((name, ms, fn) => {
    clearTimeout(timers.current[name]);
    timers.current[name] = setTimeout(fn, ms);
  }, []);

  const updateFloatingPosition = useCallback(() => {
    if (typeof window === "undefined") return;
    const runnerEl = document.getElementById("code-runner-container");
    if (runnerEl) {
      const rect = runnerEl.getBoundingClientRect();
      const targetY = rect.top + Math.min(180, Math.max(50, rect.height * 0.35));
      const calculatedBottom = Math.max(90, Math.min(window.innerHeight - 130, Math.round(window.innerHeight - targetY)));
      setFloatingBottom(calculatedBottom);
    } else {
      setFloatingBottom(Math.max(140, Math.min(window.innerHeight - 140, Math.round(window.innerHeight * 0.45))));
    }
  }, []);

  const returnHome = useCallback((openChatAfter = false) => {
    clearTimeout(timers.current["returnHome"]);
    setFloatingUp(false);
    setTaskBubble(null);
    setMood("neutral");
    setTalking(false);

    if (pendingChatFeedback.current) {
      const markdown = pendingChatFeedback.current;
      pendingChatFeedback.current = null;
      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          text: markdown,
          isTaskPraise: true,
        },
      ]);
    }

    if (openChatAfter) {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!floatingUp) return;
    const onScrollOrResize = () => updateFloatingPosition();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [floatingUp, updateFloatingPosition]);

  const speak = useCallback((text) => {
    if (!voiceRef.current) return;
    try {
      const synth = window.speechSynthesis;
      if (!synth) return;
      const u = new SpeechSynthesisUtterance(speakable(text));
      u.lang = "de-DE";
      u.rate = 1.05;
      u.pitch = 1.3;
      synth.cancel();
      if (synth.paused) synth.resume();
      synth.speak(u);
    } catch {
      // Speech synthesis unsupported - Bit just stays silent.
    }
  }, []);

  const say = useCallback(
    (text, nextMood = "neutral", nextAnim = "", action = null) => {
      setBubble({ text, action });
      setMood(nextMood);
      setTalking(true);
      later("talk", 1400, () => setTalking(false));
      if (nextAnim) {
        setAnim(nextAnim);
        later("anim", 1000, () => setAnim(""));
      }
      later("bubble", 9000, () => setBubble(null));
      speak(text);
    },
    [speak, later]
  );

  const openChat = useCallback((detail = {}) => {
    setHidden(false);
    writeFlag(SLEEP_KEY, false);
    setOpen(true);
    setBubble(null);
    if (detail.context) setContext(detail.context);
    if (detail.message) setInput(detail.message);
    if (!readFlag(DISCLAIMER_KEY)) setShowDisclaimer(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  // Other components open the chat (e.g. "Code erklären" in the editor).
  useEffect(() => {
    const handler = (e) => openChat(e.detail || {});
    window.addEventListener("open-tutor", handler);
    return () => window.removeEventListener("open-tutor", handler);
  }, [openChat]);

  // React to what happens in lessons, quizzes and challenges.
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      if (d.type === "task-checking") {
        setHidden(false);
        writeFlag(SLEEP_KEY, false);
        updateFloatingPosition();
        setFloatingUp(true);
        setMood("think");
        later("floating", 12000, () => returnHome(false));
      } else if (d.type === "task-solved" || ((d.type === "success" || d.type === "lesson-done") && d.task)) {
        failStreak.current = 0;
        setHidden(false);
        writeFlag(SLEEP_KEY, false);
        setOpen(false);

        const feedback = buildTaskSummaryAndPraise({
          task: d.task,
          code: d.code,
          language: d.language,
          lessonTitle: d.lessonTitle,
          xp: d.xp || 0,
        });

        pendingChatFeedback.current = feedback.markdown;

        setMood("happy");
        setAnim("jump");
        updateFloatingPosition();
        setFloatingUp(true);
        setTalking(true);

        setTaskBubble({
          praise: feedback.praise,
          instructions: feedback.instructions,
          focus: feedback.focus,
          xp: d.xp || 0,
          lessonTitle: d.lessonTitle,
        });

        speak(feedback.speakText);

        later("talk", 4500, () => setTalking(false));
        later("anim", 1200, () => setAnim(""));
        later("returnHome", 16000, () => returnHome(false));
      } else if (d.type === "success" || d.type === "lesson-done") {
        failStreak.current = 0;
        const base = d.text || PRAISE[Math.floor(Math.random() * PRAISE.length)];
        say(d.xp > 0 ? `${base} +${d.xp} XP.` : base, "happy", "jump");
      } else if (d.type === "hint") {
        say(d.text, "think");
      } else if (d.type === "quiz") {
        say(d.text || "Quiz abgegeben!", d.xp > 0 ? "happy" : "neutral", d.xp > 0 ? "jump" : "");
      } else if (d.type === "compile-error") {
        failStreak.current += 1;
        say("Der Compiler meckert. Soll ich dir die Fehlermeldung in einfachen Worten erklären?", "worried", "shake", {
          label: "Ja, erklär's mir",
          run: () => openChat({ context: d.context, message: "Was bedeutet diese Fehlermeldung und wie behebe ich sie?" }),
        });
      } else if (d.type === "fail") {
        failStreak.current += 1;
        if (failStreak.current >= 2) {
          say("Das ist knifflig. Soll ich dir einen Tipp geben, ohne die Lösung zu verraten?", "worried", "shake", {
            label: "Tipp holen",
            run: () => openChat({ context: d.context, message: "Gib mir bitte einen Tipp, aber nicht die komplette Lösung." }),
          });
        } else {
          say(d.text || "Noch nicht ganz. Versuch es nochmal!", "worried", "shake");
        }
      }
    };
    window.addEventListener("bit-event", handler);
    return () => window.removeEventListener("bit-event", handler);
  }, [say, openChat, later, speak, updateFloatingPosition, returnHome]);

  // Eyes follow the pointer; any activity wakes Bit up.
  useEffect(() => {
    let raf = 0;
    let pos = null;
    const track = () => {
      raf = 0;
      const el = buttonRef.current;
      if (!el || !pos) return;
      const r = el.getBoundingClientRect();
      const clamp = (v) => Math.max(-1, Math.min(1, Math.round(v * 10) / 10));
      const next = { x: clamp((pos.x - (r.left + r.width / 2)) / 260), y: clamp((pos.y - (r.top + r.height * 0.45)) / 260) };
      setLook((prev) => (prev.x === next.x && prev.y === next.y ? prev : next));
    };
    const onMove = (e) => {
      pos = { x: e.clientX, y: e.clientY };
      if (!raf) raf = requestAnimationFrame(track);
    };
    const onActivity = () => {
      lastActive.current = Date.now();
      if (idleState.current !== "active") {
        idleState.current = "active";
        setMood((m) => (m === "sleepy" ? "neutral" : m));
      }
    };
    const onPointer = (e) => {
      onMove(e);
      onActivity();
    };
    window.addEventListener("pointermove", onPointer);
    window.addEventListener("keydown", onActivity);
    return () => {
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("keydown", onActivity);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Idle nudges only make sense while working on a lesson.
  useEffect(() => {
    const inLesson = /\/lektion\//.test(location.pathname);
    if (!inLesson) return undefined;
    lastActive.current = Date.now();
    idleState.current = "active";
    const id = setInterval(() => {
      const idle = Date.now() - lastActive.current;
      if (idle > IDLE_SLEEP_MS && idleState.current !== "sleeping") {
        idleState.current = "sleeping";
        setMood("sleepy");
        setBubble({ text: "Zzz … weck mich, wenn du mich brauchst." });
      } else if (idle > IDLE_ASK_MS && idleState.current === "active") {
        idleState.current = "asked";
        say("Alles klar bei dir? Ich kann dir einen Tipp zur aktuellen Aufgabe geben.", "think", "", {
          label: "Tipp holen",
          run: () => openChat({ message: "Gib mir bitte einen Tipp zu dieser Lektion." }),
        });
      }
    }, 5000);
    return () => clearInterval(id);
  }, [location.pathname, say, openChat]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  useEffect(() => {
    const t = timers.current;
    return () => {
      Object.values(t).forEach(clearTimeout);
      try {
        window.speechSynthesis?.cancel();
      } catch {
        // ignore
      }
    };
  }, []);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    setMood("think");
    const effectiveContext = context || lastContextRef.current;
    try {
      const { data } = await api.post("/tutor/chat", {
        message: text,
        context: effectiveContext,
        session_id: sessionRef.current,
        model: "auto",
      });
      setMessages((m) => [...m, { role: "ai", text: data.answer, provider: data.provider }]);
      if (data.provider !== "none") {
        setContext(null);
      }
      setMood("happy");
      setTalking(true);
      later("talk", 1600, () => {
        setTalking(false);
        setMood("neutral");
      });
      speak(data.answer);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: formatApiError(e.response?.data?.detail) || e.message, provider: "none", isError: true }]);
      setMood("worried");
    } finally {
      setLoading(false);
    }
  };

  const toggleVoice = () => {
    const on = !voice;
    setVoice(on);
    voiceRef.current = on;
    writeFlag(VOICE_KEY, on);
    if (on) speak("Stimme an. Ich lese dir meine Antworten vor.");
    else {
      try {
        window.speechSynthesis?.cancel();
      } catch {
        // ignore
      }
    }
  };

  const putToSleep = () => {
    returnHome(false);
    setOpen(false);
    setBubble(null);
    setHidden(true);
    writeFlag(SLEEP_KEY, true);
  };

  if (hidden) {
    return (
      <button
        onClick={() => openChat()}
        data-testid="open-tutor-button"
        aria-label="Bit aufwecken"
        className="fixed z-50 flex items-center gap-2 rounded-full border border-line bg-card px-3.5 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-semibold text-foreground nb-shadow nb-press bottom-[calc(0.75rem+env(safe-area-inset-bottom,0px))] right-[calc(0.75rem+env(safe-area-inset-right,0px))] sm:bottom-5 sm:right-5"
      >
        <Moon className="h-4 w-4 text-secondary" strokeWidth={2.2} />
        Bit wecken
      </button>
    );
  }

  const bodyAnim = anim === "jump" ? "bit-jump" : anim === "shake" ? "bit-shake" : "bit-bob";

  return (
    <>
      {taskBubble && (
        <div
          role="status"
          style={{
            bottom:
              typeof window !== "undefined" && window.innerWidth >= 640
                ? `${Math.max(20, (floatingBottom || 200) - 20)}px`
                : `${Math.max(20, (floatingBottom || 160) + 84)}px`,
          }}
          className="fixed z-50 w-[calc(100vw-24px)] max-w-[360px] sm:max-w-[420px] rounded-2xl border-2 border-secondary/80 bg-[#1A1030] p-4 text-[#FDF2F8] shadow-[0_0_35px_rgba(244,114,182,0.3)] animate-pop right-[calc(0.75rem+env(safe-area-inset-right,0px))] sm:right-[124px]"
        >
          {/* Pointer tail to Bit */}
          <div className="hidden sm:block absolute -right-2.5 top-8 h-4 w-4 rotate-45 border-r-2 border-t-2 border-secondary/80 bg-[#1A1030]" />
          <div className="sm:hidden absolute right-7 -bottom-2.5 h-4 w-4 rotate-45 border-b-2 border-r-2 border-secondary/80 bg-[#1A1030]" />

          <button
            onClick={() => returnHome(false)}
            aria-label="Schließen"
            className="absolute right-2.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-lg text-secondary hover:bg-secondary/20 transition-colors"
          >
            <X className="h-4 w-4" strokeWidth={2.2} />
          </button>

          <div className="flex items-center gap-2 pr-6">
            <Sparkles className="h-4 w-4 shrink-0 text-secondary animate-pulse" />
            <span className="font-heading text-sm sm:text-base font-bold text-secondary">
              {taskBubble.praise}
            </span>
          </div>

          {taskBubble.instructions ? (
            <div className="mt-2.5 rounded-xl border border-secondary/30 bg-[#251545]/70 p-2.5 text-xs sm:text-[13px] leading-relaxed">
              <p className="text-[11px] font-bold uppercase tracking-wider text-secondary/80 mb-1">
                Aufgabe zusammengefasst
              </p>
              <p className="text-foreground/90 italic">
                „{taskBubble.instructions}“
              </p>
            </div>
          ) : taskBubble.lessonTitle ? (
            <div className="mt-2.5 rounded-xl border border-secondary/30 bg-[#251545]/70 p-2.5 text-xs sm:text-[13px]">
              <p className="text-foreground/90">
                Programmieraufgabe zur Lektion <strong>„{taskBubble.lessonTitle}“</strong> gemeistert!
              </p>
            </div>
          ) : null}

          {taskBubble.focus && (
            <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-accent">
              <span>🎯</span>
              <span>{taskBubble.focus}</span>
            </div>
          )}

          <p className="mt-2 text-xs sm:text-[13px] text-muted-foreground leading-normal">
            Dein Code erfüllt die Anforderung fehlerfrei. Tolle Leistung, mach weiter so! 💪
          </p>

          <div className="mt-3.5 flex items-center justify-between gap-2 border-t border-secondary/25 pt-2.5">
            {taskBubble.xp > 0 ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-warning/20 border border-warning/40 px-2.5 py-0.5 text-xs font-bold text-warning">
                +{taskBubble.xp} XP
              </span>
            ) : <span />}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => returnHome(true)}
                className="rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
              >
                Im Chat ansehen
              </button>
              <button
                type="button"
                onClick={() => returnHome(false)}
                className="rounded-lg bg-secondary px-3 py-1 text-xs font-bold text-white hover:bg-secondary/90 shadow-sm transition-colors"
              >
                Super, danke!
              </button>
            </div>
          </div>
        </div>
      )}

      {bubble && !open && !taskBubble && (
        <div
          role="status"
          className="fixed z-50 w-[min(320px,calc(100vw-24px))] rounded-2xl rounded-br-sm border border-secondary/60 bg-[#1A1030] px-3.5 py-2.5 sm:px-4 sm:py-3 pr-9 sm:pr-10 text-[14px] sm:text-[15px] text-[#FDF2F8] nb-shadow-lg animate-pop bottom-[calc(5.8rem+env(safe-area-inset-bottom,0px))] right-[calc(0.75rem+env(safe-area-inset-right,0px))] sm:bottom-[140px] sm:right-5"
        >
          {bubble.text}
          {bubble.action && (
            <button
              onClick={bubble.action.run}
              className="mt-3 block rounded-lg border border-secondary/60 px-3 py-1.5 text-sm font-semibold text-secondary hover:bg-secondary/10"
            >
              {bubble.action.label}
            </button>
          )}
          <button
            onClick={() => setBubble(null)}
            aria-label="Sprechblase schließen"
            className="absolute right-1.5 top-1.5 flex h-8 w-8 items-center justify-center rounded-lg text-secondary hover:bg-secondary/10"
          >
            <X className="h-4 w-4" strokeWidth={2.2} />
          </button>
        </div>
      )}

      {open && (
        <section
          aria-label="Chat mit Bit"
          data-testid="tutor-panel"
          className="fixed z-50 flex h-[min(460px,calc(100dvh-200px))] sm:h-[min(520px,calc(100vh-210px))] w-[min(400px,calc(100vw-24px))] flex-col overflow-hidden rounded-2xl border border-input bg-card nb-shadow-lg animate-pop bottom-[calc(5.8rem+env(safe-area-inset-bottom,0px))] right-[calc(0.75rem+env(safe-area-inset-right,0px))] sm:bottom-[140px] sm:right-5"
        >
          <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
            <div>
              <p className="font-heading text-lg font-bold leading-tight text-foreground">Bit</p>
              <p className="text-xs text-muted-foreground">Dein Lernroboter{loading ? " · denkt nach …" : ""}</p>
            </div>
            <div className="flex gap-1.5">
              <button
                onClick={toggleVoice}
                aria-pressed={voice}
                aria-label={voice ? "Vorlesen ausschalten" : "Antworten vorlesen"}
                title={voice ? "Vorlesen ausschalten" : "Antworten vorlesen"}
                className={`flex h-10 w-10 items-center justify-center rounded-lg border ${voice ? "border-secondary/70 bg-secondary/15 text-secondary" : "border-line text-muted-foreground hover:text-foreground"}`}
              >
                {voice ? <Volume2 className="h-4 w-4" strokeWidth={2.2} /> : <VolumeX className="h-4 w-4" strokeWidth={2.2} />}
              </button>
              <button
                onClick={putToSleep}
                aria-label="Bit schlafen legen"
                title="Bit schlafen legen"
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-line text-muted-foreground hover:text-foreground"
              >
                <Moon className="h-4 w-4" strokeWidth={2.2} />
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  setFloatingUp(false);
                }}
                data-testid="close-tutor-button"
                aria-label="Chat schließen"
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-line text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" strokeWidth={2.2} />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
            {showDisclaimer && (
              <div className="space-y-2 rounded-xl border border-line bg-muted p-3 text-xs">
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Info className="h-4 w-4 shrink-0 text-primary" strokeWidth={2.2} />
                  Hinweis zu Bit
                </div>
                <div className="flex items-start gap-2 text-muted-foreground">
                  <Server className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={2.2} />
                  <span><strong className="text-foreground">Lokal gehostet</strong> – deine Fragen werden auf unserem eigenen Server beantwortet.</span>
                </div>
                <div className="flex items-start gap-2 text-muted-foreground">
                  <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" strokeWidth={2.2} />
                  <span><strong className="text-foreground">Nur diese Sitzung</strong> – der Chat ist nach dem Neuladen weg.</span>
                </div>
                <button
                  onClick={() => {
                    writeFlag(DISCLAIMER_KEY, true);
                    setShowDisclaimer(false);
                  }}
                  className="mt-1 w-full rounded-lg bg-primary py-1.5 text-xs font-semibold"
                >
                  Verstanden
                </button>
              </div>
            )}
            {context && (
              <div className="flex items-center gap-2 rounded-lg border border-warning/50 bg-warning/10 px-3 py-2 text-xs font-medium text-warning">
                <Info className="h-4 w-4 shrink-0" strokeWidth={2.2} />
                Dein Code wird als Kontext mitgeschickt.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div
                  className={`max-w-[90%] rounded-2xl px-3.5 py-2.5 text-sm ${
                    m.role === "user"
                      ? "rounded-br-sm border border-primary/40 bg-[#0E2A33] text-[#ECFEFF]"
                      : m.isTaskPraise
                      ? "rounded-bl-sm border-2 border-secondary/70 bg-[#231238] text-[#FDF2F8] shadow-[0_0_24px_rgba(244,114,182,0.22)]"
                      : "rounded-bl-sm border border-secondary/40 bg-[#1A1030] text-[#FDF2F8]"
                  }`}
                >
                  {m.role === "ai" ? <Markdown>{m.text}</Markdown> : m.text}
                  {m.provider && m.provider !== "ollama" && (
                    <div className="mt-1 text-[10px] font-semibold opacity-70">
                      {m.provider === "emergent" ? "über Cloud-Fallback beantwortet" : "KI gerade nicht erreichbar"}
                    </div>
                  )}
                  {m.role === "ai" && m.provider === "none" && lastUserQuestion && (
                    <div className="mt-2.5 pt-2 border-t border-secondary/20 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => send(lastUserQuestion)}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-secondary/60 bg-secondary/20 px-2.5 py-1 text-xs font-semibold text-secondary hover:bg-secondary/30 transition-all disabled:opacity-50"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Frage nochmal senden
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setInput(lastUserQuestion);
                          inputRef.current?.focus();
                        }}
                        disabled={loading}
                        className="inline-flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-xs font-medium text-foreground hover:bg-white/5 transition-all"
                      >
                        Bearbeiten
                      </button>
                    </div>
                  )}
                </div>
                {m.role === "user" && (
                  <div className="mt-1 flex items-center gap-2 pr-1 text-[11px] text-muted-foreground">
                    <button
                      type="button"
                      onClick={() => send(m.text)}
                      disabled={loading}
                      title="Diese Frage nochmal an Bit senden"
                      aria-label="Diese Frage nochmal an Bit senden"
                      className="inline-flex items-center gap-1 hover:text-primary transition-colors disabled:opacity-40"
                    >
                      <RotateCcw className="h-3 w-3" />
                      <span>Nochmal senden</span>
                    </button>
                    <span className="opacity-40">·</span>
                    <button
                      type="button"
                      onClick={() => {
                        setInput(m.text);
                        inputRef.current?.focus();
                      }}
                      title="In Eingabefeld laden zum Bearbeiten"
                      aria-label="In Eingabefeld laden zum Bearbeiten"
                      className="hover:text-primary transition-colors"
                    >
                      Bearbeiten
                    </button>
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm border border-secondary/40 bg-[#1A1030] px-3 py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-secondary" strokeWidth={2.2} />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5 px-4 pb-2">
            {lastUserQuestion && (
              <button
                type="button"
                onClick={() => send(lastUserQuestion)}
                disabled={loading}
                title={`Letzte Frage wiederholen: "${lastUserQuestion.slice(0, 50)}"`}
                className="min-h-[34px] flex items-center gap-1.5 rounded-full border border-primary/50 bg-primary/10 px-3 text-xs font-medium text-primary hover:bg-primary/20 disabled:opacity-50 transition-colors"
              >
                <RotateCcw className="h-3 w-3" />
                Letzte Frage wiederholen
              </button>
            )}
            {CHIPS.map((c) => (
              <button
                key={c}
                onClick={() => send(c)}
                disabled={loading}
                className="min-h-[34px] rounded-full border border-input px-3 text-xs font-medium text-foreground hover:border-primary/60 disabled:opacity-50"
              >
                {c}
              </button>
            ))}
          </div>

          <div className="flex items-end gap-2 border-t border-line p-3">
            <label htmlFor="bit-input" className="sr-only">Nachricht an Bit</label>
            <textarea
              id="bit-input"
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                } else if (e.key === "ArrowUp" && !input.trim() && lastUserQuestion) {
                  e.preventDefault();
                  setInput(lastUserQuestion);
                }
              }}
              placeholder="Frag Bit etwas …"
              data-testid="tutor-input"
              rows={1}
              maxLength={4000}
              className="max-h-24 min-h-[44px] flex-1 resize-none rounded-lg border border-input bg-[#070A12] px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              onClick={() => send()}
              disabled={loading || !input.trim()}
              data-testid="tutor-send"
              aria-label="Senden"
              className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary disabled:opacity-50"
            >
              <Send className="h-4 w-4" strokeWidth={2.2} />
            </button>
          </div>
        </section>
      )}

      <button
        ref={buttonRef}
        onClick={() => {
          if (floatingUp) {
            returnHome(true);
            return;
          }
          if (open) {
            setOpen(false);
          } else {
            openChat();
          }
        }}
        data-testid="open-tutor-button"
        aria-label={open ? "Chat mit Bit schließen" : "Chat mit Bit öffnen"}
        aria-expanded={open}
        style={floatingUp && floatingBottom ? { bottom: `${floatingBottom}px` } : {}}
        className={`fixed z-50 block rounded-[22px] sm:rounded-[26px] border bg-card p-1 nb-shadow-lg transition-all duration-1000 cubic-bezier(0.25, 1, 0.5, 1) touch-manipulation right-[calc(0.75rem+env(safe-area-inset-right,0px))] sm:right-5 ${
          floatingUp
            ? "bit-floating-active border-primary shadow-[0_0_25px_rgba(34,211,238,0.35)]"
            : "bottom-[calc(0.75rem+env(safe-area-inset-bottom,0px))] sm:bottom-5 border-input hover:border-line"
        }`}
      >
        <span className={`block ${floatingUp ? "bit-floating-body" : bodyAnim}`}>
          <BitAvatar
            mood={mood}
            lookX={floatingUp ? -0.8 : look.x}
            lookY={floatingUp ? 0 : look.y}
            talking={talking}
            size={92}
            className="w-[72px] h-[72px] sm:w-[92px] sm:h-[92px]"
          />
        </span>
      </button>
    </>
  );
}
